import { useEffect, useRef, useState } from "react";
import toast from "react-hot-toast";
import { accountService } from "../services/accountService";
import { supabase } from "../services/supabase";
import { useAccountsStore } from "../stores/accountsStore";

/**
 * 🐛 Migration hook – mută conturile din localStorage (SettingsStore) în Supabase
 *
 * 🔥 Se rulează o singură dată după ce user-ul este autentificat.
 * 1. Obține conturile locale persistate (zustand persist → localStorage)
 * 2. Fetch-uiește conturile din Supabase
 * 3. Dacă remote e gol și local există ➜ creează conturile remote
 * 4. Dacă remote există ➜ preferă remote și sincronizează store-ul local (urmează refactor în subtask 3.4)
 * 5. Marchează în localStorage flag `accountsMigrated` pentru a evita rerulare inutilă
 */
export function useAccountMigration() {
  const { accounts } = useAccountsStore();
  const [status, setStatus] = useState<"idle" | "migrating" | "done" | "error">("idle");
  const hasRun = useRef(false);

  useEffect(() => {
    if (hasRun.current) return; // rulează o singură dată
    hasRun.current = true;

    const runMigration = async () => {
      try {
        const session = await supabase.auth.getSession();
        const user = session.data.session?.user;
        if (!user) {
          return; // nu avem user, nu facem migrare
        }
        const userId = user.id;

        // Dacă deja migrat (flag), ieșim
        if (localStorage.getItem("accountsMigrated") === "true") {
          return;
        }

        setStatus("migrating");

        // 1. Fetch remote
        const remoteAccounts = await accountService.fetchAccounts(userId);

        if (remoteAccounts.length === 0 && accounts.length > 0) {
          // Migrare local ➜ remote
          for (const acc of accounts) {
            try {
              await accountService.createAccount(userId, {
                name: acc.accountName ?? acc.accountId,
                account_type: acc.accountType ?? "CHECKING",
                initial_balance: acc.initialBalance ?? 0,
                included_in_total: acc.includedInTotal ?? true,
                display_order: acc.displayOrder ?? 0,
              });
            } catch (err) {
              console.error("[AccountMigration] Eroare la migrarea contului", acc, err);
            }
          }
          toast.success("Conturile au fost migrate pe server ✅");
        }

        // Marchează flag
        localStorage.setItem("accountsMigrated", "true");
        setStatus("done");
      } catch (err) {
        console.error("[AccountMigration] Eroare migration hook", err);
        setStatus("error");
      }
    };

    runMigration();
  }, [accounts]);

  return status;
}

export default useAccountMigration;
